import { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Alert,
} from '@mui/material'
import { CloudUpload, CreateNewFolder, Delete } from '@mui/icons-material'

export interface UploadItem {
  file: File
  relativePath: string
}

interface UploadDialogProps {
  open: boolean
  onClose: () => void
  onUpload: (items: UploadItem[]) => Promise<void>
}

export function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const all: FileSystemEntry[] = []
    const readBatch = () => {
      reader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            resolve(all)
          } else {
            all.push(...entries)
            readBatch()
          }
        },
        (error) => reject(error),
      )
    }
    readBatch()
  })
}

export async function traverseEntry(entry: FileSystemEntry, basePath: string): Promise<UploadItem[]> {
  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry
    return new Promise((resolve) => {
      fileEntry.file((file) => {
        resolve([{ file, relativePath: basePath ? `${basePath}/${file.name}` : file.name }])
      })
    })
  }
  if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry
    const entries = await readAllEntries(dirEntry.createReader())
    const items: UploadItem[] = []
    const childBase = basePath ? `${basePath}/${dirEntry.name}` : dirEntry.name
    for (const child of entries) {
      const childItems = await traverseEntry(child, childBase)
      items.push(...childItems)
    }
    return items
  }
  return []
}

export default function UploadDialog({ open, onClose, onUpload }: UploadDialogProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const folderInputRef = useRef<HTMLInputElement>(null)
  const [items, setItems] = useState<UploadItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const newItems: UploadItem[] = []
    const entries = Array.from(e.dataTransfer.items)
      .map((item) => item.webkitGetAsEntry?.())
      .filter((entry): entry is FileSystemEntry => entry !== null)

    if (entries.length > 0) {
      for (const entry of entries) {
        const traversed = await traverseEntry(entry, '')
        newItems.push(...traversed)
      }
    } else {
      for (const file of Array.from(e.dataTransfer.files)) {
        newItems.push({ file, relativePath: file.name })
      }
    }

    if (newItems.length > 0) {
      setItems((prev) => [...prev, ...newItems])
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length > 0) {
      const newItems = selected.map((file) => ({
        file,
        relativePath: file.webkitRelativePath || file.name,
      }))
      setItems((prev) => [...prev, ...newItems])
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [])

  const handleFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || [])
    if (selected.length > 0) {
      const newItems = selected.map((file) => ({
        file,
        relativePath: file.webkitRelativePath || file.name,
      }))
      setItems((prev) => [...prev, ...newItems])
    }
    if (folderInputRef.current) folderInputRef.current.value = ''
  }, [])

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleUpload = useCallback(async () => {
    if (items.length === 0) return
    setUploading(true)
    setError('')
    try {
      await onUpload(items)
      setItems([])
    } catch {
      setError(t('files.uploadError'))
    } finally {
      setUploading(false)
    }
  }, [items, onUpload, t])

  const handleClose = useCallback(() => {
    if (!uploading) {
      setItems([])
      setError('')
      onClose()
    }
  }, [uploading, onClose])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('files.upload')}</DialogTitle>
      <DialogContent>
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: '2px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            mb: 2,
            '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          }}
        >
          <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="body1">
            {t('files.dropFiles')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current?.click()}
            >
              {t('files.selectFiles')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<CreateNewFolder />}
              onClick={() => folderInputRef.current?.click()}
            >
              {t('files.selectFolder')}
            </Button>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileSelect}
          />
          <input
            ref={folderInputRef}
            type="file"
            multiple
            hidden
            // @ts-expect-error webkitdirectory is non-standard but widely supported
            webkitdirectory=""
            directory=""
            onChange={handleFolderSelect}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
              {t('common.loading')}...
            </Typography>
          </Box>
        )}

        {items.length > 0 && (
          <List dense>
            {items.map((item, index) => (
              <ListItem
                key={index}
                secondaryAction={
                  <IconButton edge="end" onClick={() => removeItem(index)} disabled={uploading}>
                    <Delete />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={item.relativePath || item.file.name}
                  secondary={`${(item.file.size / 1024).toFixed(1)} KB`}
                />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={items.length === 0 || uploading}
        >
          {t('files.upload')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}